/**
 * Thin Shell Client for App (Plugin Mode)
 *
 * Wraps the bridge's low-level call() API into convenient async methods
 * for storage, auth callback, file system, etc.
 * Only active when running inside a plugin webview.
 */

import { bridge } from './index'

class ThinShellClient {
  async getStorageItem(key: string): Promise<string | null> {
    const result = await bridge.call<{ value: string | null }>('storage', 'get', { key })
    if (!result.success) {
      console.warn('Failed to get storage item:', result.error)
      return null
    }
    return result.data?.value ?? null
  }

  async setStorageItem(key: string, value: string): Promise<void> {
    const result = await bridge.call('storage', 'set', { key, value })
    if (!result.success) {
      throw new Error(result.error ?? 'Failed to set storage item')
    }
  }

  async deleteStorageItem(key: string): Promise<void> {
    const result = await bridge.call('storage', 'delete', { key })
    if (!result.success) {
      throw new Error(result.error ?? 'Failed to delete storage item')
    }
  }

  async openExternal(url: string): Promise<void> {
    const result = await bridge.call('ui', 'openExternal', { url })
    if (!result.success) {
      window.open(url, '_blank')
    }
  }

  async startAuthCallbackServer(): Promise<{ callbackUrl: string; state: string }> {
    const result = await bridge.call<{ callbackUrl: string; state: string }>(
      'auth',
      'startCallbackServer',
      {}
    )
    if (!result.success || !result.data) {
      throw new Error(result.error ?? 'Failed to start auth callback server')
    }
    return { callbackUrl: result.data.callbackUrl, state: result.data.state }
  }

  async stopAuthCallbackServer(): Promise<void> {
    await bridge.call('auth', 'stopCallbackServer', {})
  }

  async createArchive(
    sourcePath: string,
    files: string[],
    options?: { prefix?: string }
  ): Promise<{ base64Content: string; fileCount: number; size: number }> {
    const result = await bridge.call<{ base64Content: string; fileCount: number; size: number }>(
      'archive',
      'createTarGz',
      { sourcePath, files, prefix: options?.prefix }
    )
    if (!result.success || !result.data) {
      throw new Error(result.error ?? 'Failed to create archive')
    }
    return result.data
  }

  async createArchiveToFile(
    sourcePath: string,
    files: string[],
    outputPath: string,
    options?: { prefix?: string }
  ): Promise<{ filePath: string; fileCount: number; size: number }> {
    const result = await bridge.call<{ filePath: string; fileCount: number; size: number }>(
      'archive',
      'createTarGz',
      { sourcePath, files, prefix: options?.prefix, outputPath }
    )
    if (!result.success || !result.data) {
      throw new Error(result.error ?? 'Failed to create archive')
    }
    return result.data
  }

  async readBinaryChunk(path: string, offset: number, length: number): Promise<string> {
    const result = await bridge.call<{ content: string }>('fs', 'readBinary', {
      path,
      offset,
      length
    })
    if (!result.success || !result.data) {
      throw new Error(result.error ?? 'Failed to read binary chunk')
    }
    return result.data.content
  }

  async deleteFile(path: string): Promise<void> {
    const result = await bridge.call('fs', 'delete', { path })
    if (!result.success) {
      throw new Error(result.error ?? 'Failed to delete file')
    }
  }

  // ==========================================================================
  // Process Primitives (managed child processes)
  // ==========================================================================

  async spawnProcess(
    command: string,
    options?: { cwd?: string; env?: Record<string, string>; label?: string }
  ): Promise<{ id: string; pid: number }> {
    const result = await bridge.call<{ id: string; pid: number }>('process', 'spawn', {
      command,
      ...options
    })
    if (!result.success || !result.data) {
      throw new Error(result.error ?? 'Failed to spawn process')
    }
    return result.data
  }

  async killProcess(id: string, signal?: string): Promise<void> {
    const result = await bridge.call('process', 'kill', { id, signal })
    if (!result.success) {
      throw new Error(result.error ?? 'Failed to kill process')
    }
  }

  async getProcessStatus(
    id?: string
  ): Promise<
    Array<{ id: string; pid: number; running: boolean; exitCode: number | null; label?: string }>
  > {
    const result = await bridge.call<{
      processes: Array<{
        id: string
        pid: number
        running: boolean
        exitCode: number | null
        label?: string
      }>
    }>('process', 'status', id ? { id } : {})
    if (!result.success || !result.data) {
      return []
    }
    return result.data.processes
  }

  async getProcessOutput(id: string, tail?: number): Promise<string[]> {
    const result = await bridge.call<{ lines: string[] }>('process', 'output', { id, tail })
    if (!result.success || !result.data) {
      return []
    }
    return result.data.lines
  }

  isInPlugin(): boolean {
    return bridge.isInPlugin()
  }
}

export const shell = new ThinShellClient()
